import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./InkPaywall.module.scss"

/**
 * InkPaywall (`/ink`) — an original, self-contained ONBOARDING paywall for
 * Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md. Concept doc: claude-paywalls/ink.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority; the whole concept is built to it — told as INVISIBLE INK.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Written in invisible ink." Your first memories lie on a warm page as BLANK
 * frames — to anyone who picks up the page there is simply nothing to read.
 * Press and move across the page: a warmth halo follows your hand and each
 * memory blooms into full colour under your touch — then FADES BACK to blank the
 * instant you lift. The transient reveal is the privacy proof: the photos are
 * always there, but they are legible to no one but you, and only while your own
 * hand is on them. Press "Keep them for my eyes" and a warm wash inks the whole
 * page in, a wax seal presses down, and the real protections light up.
 *
 * ── Divergence (vs. the mined security/privacy cluster) ─────────────────────
 * Layout      : an organic SCATTER of loose photo frames on a warm page — no
 *               hero image, no card-first, no film strip (Negative), no contact
 *               sheet (Cleared), no window/blinds (Shade), no keypad (Only You).
 * Interaction : a TRANSIENT press-and-move HEAT-HALO reveal that fades on lift
 *               — new to the set: Negative's loupe develop is permanent+1D along
 *               a strip; this is a 2D bloom that recedes, proving "only in hand".
 * Register    : intimate / secret / quiet-wonder (a whispered confidence), not
 *               Negative's analog-craft ritual or Cleared's wry officialdom.
 * Art         : warm invisible-ink parchment; brand blue = the seal/protected
 *               accent, amber = the warmth of your own hand. Light and dark.
 * Value frame : legibility-to-you-alone (encryption/lock made visceral).
 *
 * Honest: a metaphor for the real protections (Face ID / PIN, kept out of the
 * camera roll & shared albums, private encrypted backup). Strictly additive —
 * the page's "fade to blank" is privacy, never data loss ("they never leave").
 * No fake urgency/scarcity. Yearly is the featured, default plan.
 */

// Loose photos on the page — reuse existing personal-memory photos.
// x/y are the frame CENTRE as a % of the page box; rot in deg; w in px.
type Frame = { id: string; x: number; y: number; rot: number; w: number }
const FRAMES: Frame[] = [
  { id: "m04", x: 22, y: 26, rot: -6, w: 118 },
  { id: "m09", x: 60, y: 20, rot: 5, w: 128 },
  { id: "m14", x: 84, y: 40, rot: -4, w: 104 },
  { id: "m07", x: 32, y: 58, rot: 4, w: 132 },
  { id: "m11", x: 68, y: 66, rot: -7, w: 120 },
  { id: "m02", x: 15, y: 78, rot: 7, w: 100 },
  { id: "m17", x: 90, y: 76, rot: 6, w: 96 },
]

// What your key actually protects — real product features, stated plainly.
const PROTECTS = [
  { k: "lock", label: "Opens only with Face ID or your PIN" },
  { k: "roll", label: "Never in your camera roll or shared albums" },
  { k: "cloud", label: "Private, encrypted cloud backup" },
]

// px radius of the warmth halo that inks memories in under your touch.
const HALO = 132

type PlanKey = "yearly" | "monthly"

const InkPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // reveal[i] in 0..1 per frame; sealed = committed (permanent, all inked).
  const [reveals, setReveals] = useState<number[]>(() =>
    FRAMES.map(() => (reduceMotion ? 1 : 0)),
  )
  const [sealed, setSealed] = useState<boolean>(!!reduceMotion)
  const [fading, setFading] = useState(false)
  const [halo, setHalo] = useState<{ x: number; y: number; on: boolean }>({
    x: 0,
    y: 0,
    on: false,
  })
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)

  const pageRef = useRef<HTMLDivElement>(null)
  const touched = useRef(false)
  const sealedRef = useRef(false)
  const rafRef = useRef<number>(0)
  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)

  const stopAuto = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
  }

  // reveal frames whose centre is within the warmth halo of (px,py) [page-local].
  const revealAt = (px: number, py: number) => {
    const el = pageRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setReveals(
      FRAMES.map((f) => {
        const fx = (f.x / 100) * r.width
        const fy = (f.y / 100) * r.height
        const d = Math.hypot(px - fx, py - fy)
        const v = 1 - d / HALO
        // ease so the bloom is soft-edged, brightest at the halo centre
        return v <= 0 ? 0 : v >= 1 ? 1 : v * v * (3 - 2 * v)
      }),
    )
  }

  // Seal: commit the page — every memory inks in for good, the wax seal presses.
  const seal = useCallback(() => {
    sealedRef.current = true
    touched.current = true
    stopAuto()
    setFading(false)
    setHalo((h) => ({ ...h, on: false }))
    setReveals(FRAMES.map(() => 1))
    setSealed(true)
  }, [])

  // Auto-demo: a scripted hand sweeps the page (revealing/fading), then seals so
  // passive viewers still land on the full payoff. Honours reduced motion.
  const autoDemo = useCallback(() => {
    if (touched.current || reduceMotion || sealedRef.current) return
    native.logMixpanelEvent("html_ink_autodemo")
    const el = pageRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    // waypoints in page-local px, tracing across the scattered frames
    const pts = [
      [0.2, 0.28],
      [0.58, 0.2],
      [0.85, 0.42],
      [0.34, 0.58],
      [0.68, 0.68],
      [0.5, 0.5],
    ].map(([nx, ny]) => [nx * r.width, ny * r.height])

    const SEG = 460 // ms per segment
    let start = 0
    const step = (t: number) => {
      if (touched.current || sealedRef.current) return
      if (!start) start = t
      const elapsed = t - start
      const total = SEG * (pts.length - 1)
      if (elapsed >= total) {
        // settle on the last point, then seal
        const [lx, ly] = pts[pts.length - 1]
        setHalo({ x: lx, y: ly, on: true })
        revealAt(lx, ly)
        pushTimer(window.setTimeout(seal, 420))
        return
      }
      const i = Math.floor(elapsed / SEG)
      const f = (elapsed - i * SEG) / SEG
      const [ax, ay] = pts[i]
      const [bx, by] = pts[i + 1]
      const x = ax + (bx - ax) * f
      const y = ay + (by - ay) * f
      setHalo({ x, y, on: true })
      revealAt(x, y)
      rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion, seal])

  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setShowClose(true), 2400))
    pushTimer(window.setTimeout(autoDemo, 2900))
    return () => {
      stopAuto()
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const beginTouch = (clientX: number, clientY: number) => {
    if (reduceMotion || sealedRef.current) return
    if (!touched.current) {
      touched.current = true
      stopAuto()
      native.logMixpanelEvent("html_ink_reveal_touch")
    }
    setFading(false)
    const el = pageRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const px = clientX - r.left
    const py = clientY - r.top
    setHalo({ x: px, y: py, on: true })
    revealAt(px, py)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    beginTouch(e.clientX, e.clientY)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (e.buttons === 0 || sealedRef.current) return
    beginTouch(e.clientX, e.clientY)
  }
  // Lift: the ink recedes — memories fade back to blank (privacy, not loss).
  const onPointerUp = () => {
    if (sealedRef.current) return
    setHalo((h) => ({ ...h, on: false }))
    setFading(true)
    setReveals(FRAMES.map(() => 0))
    pushTimer(window.setTimeout(() => setFading(false), 650))
  }

  const revealedCount = reveals.filter((v) => v > 0.55).length
  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_ink_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  return (
    <div className={`${styles.root} ${sealed ? styles.rootSealed : ""}`}>
      <div className={styles.grain} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Written in invisible ink</span>
        <h1 className={styles.title}>
          Blank to everyone.
          <br />
          Vivid only to you.
        </h1>
        <p className={styles.sub}>
          In your vault, every memory reads like a blank page — until it&rsquo;s
          in your own hands. Press across the page: they bloom into colour under
          your touch, and recede the moment you let go.
        </p>
      </div>

      {/* ---------- the invisible-ink page ---------- */}
      <div
        className={`${styles.page} ${fading ? styles.pageFading : ""}`}
        ref={pageRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        role="slider"
        aria-label="Reveal your memories"
        aria-valuemin={0}
        aria-valuemax={FRAMES.length}
        aria-valuenow={revealedCount}
      >
        <div className={styles.pageEdge} aria-hidden="true" />

        {/* warmth halo that follows your hand */}
        {!reduceMotion && (
          <motion.div
            className={styles.halo}
            aria-hidden="true"
            animate={{
              opacity: halo.on && !sealed ? 1 : 0,
              x: halo.x,
              y: halo.y,
            }}
            transition={{
              opacity: { duration: 0.28 },
              x: { duration: 0 },
              y: { duration: 0 },
            }}
          />
        )}

        {FRAMES.map((f, i) => (
          <div
            key={f.id}
            className={styles.frame}
            style={{
              left: `${f.x}%`,
              top: `${f.y}%`,
              width: f.w,
              transform: `translate(-50%, -50%) rotate(${f.rot}deg)`,
            }}
          >
            <div className={styles.frameInner}>
              <div
                className={styles.photo}
                style={{
                  backgroundImage: `url(/byheart/${f.id}.jpg)`,
                  opacity: sealed ? 1 : reveals[i],
                }}
              />
              <div className={styles.blank} aria-hidden="true">
                <span className={styles.blankLine} />
                <span className={styles.blankLine} />
                <span className={styles.blankLine} />
              </div>
              <span
                className={`${styles.kept} ${sealed ? styles.keptOn : ""}`}
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" width="11" height="11">
                  <path
                    d="M5 12.5l4 4 10-10.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
            <span className={styles.frameNo}>
              {String(i + 1).padStart(2, "0")}
            </span>
          </div>
        ))}

        {/* wax seal seats when you keep them for good */}
        <motion.div
          className={styles.seal}
          aria-hidden={!sealed}
          initial={false}
          animate={
            sealed
              ? { opacity: 1, scale: 1, rotate: -7 }
              : { opacity: 0, scale: 1.6, rotate: -20 }
          }
          transition={{ type: "spring", stiffness: 320, damping: 17 }}
        >
          <span className={styles.sealInner}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M7 10V8a5 5 0 0 1 10 0v2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
              />
              <circle cx="12" cy="14.4" r="1.5" fill="currentColor" />
            </svg>
            <em>
              KEPT
              <span>· ONLY YOU ·</span>
            </em>
          </span>
        </motion.div>
      </div>

      {/* ---------- hint / honesty ---------- */}
      <div className={styles.hint} aria-live="polite">
        {sealed ? (
          <span className={styles.hintDone}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Kept for your eyes — unreadable to anyone else
          </span>
        ) : (
          <button
            className={styles.sealBtn}
            onClick={seal}
            disabled={!!reduceMotion}
          >
            <span className={styles.sealBtnDot} aria-hidden="true" />
            Keep them for my eyes
          </button>
        )}
        <span className={styles.honest}>
          They never leave your vault — they&rsquo;re just legible to no one but
          you.
        </span>
      </div>

      {/* ---------- protections ---------- */}
      <div className={styles.protects}>
        {PROTECTS.map((p, i) => (
          <span
            key={p.k}
            className={`${styles.protect} ${sealed ? styles.protectOn : ""}`}
            style={{ transitionDelay: `${120 + i * 90}ms` }}
          >
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {p.label}
          </span>
        ))}
      </div>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={handleSubscribe}>
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Try 7 days free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default InkPaywall
